import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock3,
  MessageCircleMore,
  Send,
  Sparkles,
  UserRoundCheck,
} from "lucide-react"
import { useEffect, useState } from "react"

const stepDuration = 2200
const steps = [
  {
    title: "Support report arrives from Slack",
    detail: "Resolto opens a structured case and records the fictional source.",
  },
  {
    title: "The affected account is identified",
    detail: "The report is linked to Atlas Components and its approved customer ID.",
  },
  {
    title: "Approved evidence is gathered",
    detail: "Demo billing, account, and sync records enter one cited timeline.",
  },
  {
    title: "One accountable owner is assigned",
    detail: "Operations receives the case with the original request and evidence.",
  },
  {
    title: "The failed sync is exposed",
    detail: "Payment succeeded, while the entitlement update did not complete.",
  },
  {
    title: "The finding shows its confidence",
    detail: "Confirmed facts and remaining uncertainty stay visibly separate.",
  },
  {
    title: "A bounded action is proposed",
    detail: "The preview shows the allowlisted field change and expected effect.",
  },
  {
    title: "A human approves execution",
    detail: "The required authority reviews the evidence and proposed mutation.",
  },
  {
    title: "Resolto verifies the outcome",
    detail: "The re-read result and complete audit history mark the case resolved.",
  },
] as const

export function GuidedProductPreview() {
  const [active, setActive] = useState(0)
  const [reduceMotion, setReduceMotion] = useState(false)
  const current = steps[active] ?? steps[0]

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updatePreference = () => {
      setReduceMotion(query.matches)

      if (query.matches) {
        setActive(steps.length - 1)
      }
    }

    updatePreference()
    query.addEventListener("change", updatePreference)

    return () => query.removeEventListener("change", updatePreference)
  }, [])

  useEffect(() => {
    if (reduceMotion) {
      return
    }

    const timer = window.setInterval(() => {
      setActive((step) => (step + 1) % steps.length)
    }, stepDuration)

    return () => window.clearInterval(timer)
  }, [reduceMotion])

  return (
    <section className="guided-preview" aria-label="Guided Resolto workflow">
      <div className="guided-preview__topbar">
        <div>
          <span className="guided-preview__status" />
          <strong>Northstar SaaS</strong>
          <small>Fictional demo workspace</small>
        </div>
        <span>Live product sequence · Fictional data only</span>
      </div>

      <div className="guided-preview__workspace">
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: this scrollable case queue needs keyboard access. */}
        <aside className="guided-preview__queue" aria-label="Open support cases" tabIndex={0}>
          <div>
            <strong>Open cases</strong>
            <span>4 open</span>
          </div>
          <article data-active="true">
            <MessageCircleMore aria-hidden="true" />
            <div>
              <strong>Paid account has starter access</strong>
              <span>Slack · Atlas Components</span>
            </div>
            <small>{active < 8 ? "Urgent" : "Resolved"}</small>
          </article>
          <article>
            <div>
              <strong>Login after email change</strong>
              <span>Web · Meridian Labs</span>
            </div>
            <small>Normal</small>
          </article>
          <article>
            <div>
              <strong>Monthly report missing</strong>
              <span>Slack · Cobalt Freight</span>
            </div>
            <small>Normal</small>
          </article>
        </aside>

        <div className="guided-preview__conversation">
          <header>
            <div>
              <strong>Atlas Components</strong>
              <span>Case RES-1042 · Slack</span>
            </div>
            <span data-risk={active >= 4 && active < 8}>
              <Clock3 aria-hidden="true" />
              {active >= 8
                ? "Outcome verified"
                : active >= 4
                  ? "Finding ready"
                  : "Investigation active"}
            </span>
          </header>
          <div className="guided-preview__messages">
            <p className="guided-preview__customer-message">
              Atlas paid yesterday, but the workspace still shows starter access. Can someone check
              what failed?
            </p>

            <div key={active} className="guided-preview__event" aria-live="polite">
              {active === 0 && <MessageCircleMore aria-hidden="true" />}
              {active === 1 && <AlertTriangle aria-hidden="true" />}
              {active === 2 && <Sparkles aria-hidden="true" />}
              {active === 3 && <UserRoundCheck aria-hidden="true" />}
              {active === 4 && <Clock3 aria-hidden="true" />}
              {active === 5 && <CheckCircle2 aria-hidden="true" />}
              {active === 6 && <Bot aria-hidden="true" />}
              {active === 7 && <Send aria-hidden="true" />}
              {active === 8 && <CheckCircle2 aria-hidden="true" />}
              <div>
                <strong>{current.title}</strong>
                <span>{current.detail}</span>
              </div>
            </div>

            {active >= 6 && (
              <div className="guided-preview__draft">
                <div>
                  <Bot aria-hidden="true" />
                  <strong>Recommended next step</strong>
                  <span>Human approval required</span>
                </div>
                <p>
                  Re-run the allowlisted entitlement sync for cus_NST_2048, then re-read access
                  before marking this case resolved.
                </p>
                <div>
                  <span>Billing fixture</span>
                  <span>Account record</span>
                  {active >= 7 && (
                    <strong>
                      <CheckCircle2 aria-hidden="true" />
                      Approved and verified
                    </strong>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="guided-preview__context">
          <div>
            <span>Owner</span>
            <strong>{active >= 3 ? "Jon Bell" : "Unassigned"}</strong>
          </div>
          <div>
            <span>Investigation</span>
            <strong>{active >= 2 ? "Billing entitlement" : "Pending"}</strong>
          </div>
          <div data-visible={active >= 5} aria-hidden={active < 5}>
            <span>Evidence</span>
            <strong>4 approved demo records</strong>
            <small>Billing, account, sync event, playbook</small>
          </div>
          <div className="guided-preview__tower">
            <span>Resolution state</span>
            <strong>{active >= 8 ? "Verified" : "In progress"}</strong>
            <small>{active >= 8 ? "Audit trail complete" : "Approval may be required"}</small>
          </div>
        </aside>
      </div>
    </section>
  )
}
